import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { categories, getComicsByCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    setActiveSlug(slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const filteredComics = useMemo(() => {
    let result = getComicsByCategory(activeSlug);
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    return result;
  }, [activeSlug, sortBy]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = currentCategory?.name || '全部';

  const categoryDescriptions: Record<string, string> = {
    all: '浏览全部韩国漫画作品，包括恋爱、奇幻、动作、搞笑、悬疑、日常、剧情、恐怖、BL等多种类型，韩漫网站为您提供最全的韩国漫画大全。',
    romance: '精选恋爱韩国漫画，甜蜜浪漫的爱情故事、穿越宫廷的奇幻恋爱，好看的韩国恋爱漫画尽在韩漫网站。',
    fantasy: '奇幻韩国漫画大全，魔法世界、剑与冒险、末世生存，免费阅读最精彩的韩国奇幻漫画。',
    action: '热血动作韩国漫画，战斗升级、武侠重生，高燃的韩国动作漫画带给您极致阅读体验。',
    comedy: '搞笑韩国漫画推荐，轻松幽默的日常喜剧、爆笑职场变身故事，让您在欢笑中享受漫画乐趣。',
    thriller: '悬疑韩国漫画精选，心理惊悚、诡异案件、层层谜团，扣人心弦的韩国悬疑漫画等您来探索。',
    'slice-of-life': '日常韩国漫画，温暖治愈的生活片段、平凡中的美好瞬间，免费阅读最温馨的韩国日常漫画。',
    drama: '剧情韩国漫画，深刻的人性探讨、复杂的人物关系、引人深思的故事，精选最好看的韩国剧情漫画。',
    horror: '恐怖韩国漫画，毛骨悚然的超自然故事、深渊中的低语，胆大就来挑战韩国恐怖漫画。',
    bl: 'BL韩国漫画推荐，唯美细腻的男男爱情故事，古风历史与现代都市的浪漫邂逅。',
  };

  const description = categoryDescriptions[activeSlug] || categoryDescriptions.all;
  const title = activeSlug === 'all' ? '漫画分类 - 全部韩国漫画' : `${categoryName}漫画 - 韩漫网站`;

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}韩国漫画`,
    description: description,
    url: `https://hanman.com/category/${activeSlug}`,
    hasPart: filteredComics.map((comic, i) => ({
      '@type': 'ComicStory',
      name: comic.title,
      author: { '@type': 'Person', name: comic.author },
      position: i + 1,
    })),
  };

  const sortOptions = [
    { value: 'latest', label: '最新' },
    { value: 'popular', label: '人气' },
    { value: 'rating', label: '评分' },
  ];

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        keywords={`${categoryName}漫画, 韩国漫画, 韩漫网站, 免费阅读漫画, 下拉式, 好看漫画`}
        canonicalPath={`/category/${activeSlug}`}
        schema={categorySchema}
      />

      <div className="pt-16">
        <div className="w-full px-4 md:px-8 py-6 bg-background-50 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '漫画分类', path: '/category/all' },
                ...(activeSlug !== 'all' ? [{ label: categoryName }] : []),
              ]}
            />

            <div className="mt-2">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {activeSlug === 'all' ? '全部韩国漫画' : `${categoryName}漫画`}
              </h1>
              <p className="mt-2 text-sm text-foreground-500 max-w-2xl">
                {description}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-6 bg-background-50 border-b border-background-200/60 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-1.5 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value as typeof sortBy)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-background-50 text-foreground-900 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-10">
          <div className="max-w-7xl mx-auto">
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-book-2-line text-2xl text-foreground-400"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-700">暂无漫画</h3>
                <p className="mt-2 text-sm text-foreground-500">该分类下暂无漫画作品，请浏览其他分类</p>
                <Link
                  to="/category/all"
                  className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  浏览全部漫画
                </Link>
              </div>
            )}

            <div className="mt-10 text-center">
              <p className="text-sm text-foreground-400">
                共找到 <strong className="text-foreground-600">{filteredComics.length}</strong> 部漫画作品
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}